import React, { FunctionComponent, useEffect, useState } from 'react';
import FileDownload from 'js-file-download';
import { useParams } from 'react-router-dom';
import {
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  IconButton,
  Stack,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import SaveIcon from '@mui/icons-material/Save';
import DownloadIcon from '@mui/icons-material/Download';
import FileUploadIcon from '@mui/icons-material/FileUpload';
import DeleteIcon from '@mui/icons-material/Delete';
import { IAlertProps } from '../../interface/IAlertProps';
import { FormatTestJsonToText, getDefaultAlertProps } from '../../utils/utils';
import { IApiResult } from '../../interface/IApiResult';
import {
  API_STATUS_PROBLEM,
  DOWNLOAD_FILE_NUM,
  TEST_TYPE,
} from '../../constant/constants';
import { AlertSeverityEnum } from '../../enum/AlertSeverityEnum';
import { MyAlert } from '../UI/MyAlert';
import { ContentDto } from '../../dto/ContentDto';
import { TestDto } from '../../dto/TestDto';
import { apiGetTest } from '../../service/apiGetTest';
import { DialogTestItem } from './DialogTestItem';
import { apiDeleteContent } from '../../service/apiDeleteContent';
import { AddFile } from '../add-file/AddFile';
import { BackDropDiv } from '../backdrop/BackDropDiv';
import { apiGetTestNew } from '../../service/apiGetTestNew';
import { MyTextFiled } from '../UI/MyTextField';
import { apiGetTestFile } from '../../service/apiGetTestFile';
import { TextDto } from '../../dto/TextDto';
import { apiSaveTestNew } from '../../service/apiSaveTestNew';

interface Props {
  open: boolean;
  isEdit?: boolean;
  onClose: () => void;
  dto?: ContentDto;
}

export const DialogTestNew: FunctionComponent<Props> = ({
  open,
  dto,
  isEdit,
  onClose,
}) => {
  const [alertProps, setAlertProps] = useState<IAlertProps>(
    getDefaultAlertProps()
  );
  const [testString, setTestString] = useState('');
  const [testDtos, setTestDtos] = useState<TestDto[]>([]);
  const [backDrop, setBackDrop] = useState(false);
  let numTest = 0;

  const handleClose = () => {
    setTestDtos([]);
    onClose();
  };
  const { section } = useParams();

  const getTestFile = async (route: string, name: string, test: TestDto[]) => {
    const apiResult: IApiResult = await apiGetTestFile(route, name);
    if (apiResult.isSuccess) {
      if (apiResult.data === '') {
        setTestString(FormatTestJsonToText(test));
      } else {
        setTestString(apiResult.data);
      }
    } else if (apiResult.status !== 404) {
      setAlertProps({
        message: API_STATUS_PROBLEM,
        severity: AlertSeverityEnum.error,
      });
    }
  };

  const getTest = async (route: string, key: string, download?: number) => {
    setBackDrop(true);
    const apiResult: IApiResult = await apiGetTestNew(route, key, download);
    if (apiResult.isSuccess) {
      if (apiResult?.data?.test && apiResult.data.test !== '') {
        setTestDtos(apiResult.data.test);
      }
    } else {
      setAlertProps({
        message: API_STATUS_PROBLEM,
        severity: AlertSeverityEnum.error,
      });
    }
    if (apiResult?.data?.test && apiResult.data.test !== '') {
      await getTestFile(route, key, apiResult.data.test);
    }
    setBackDrop(false);
  };

  const saveText = async (route: string, key: string, title: string) => {
    const textDto: TextDto = {
      text: testString,
    };
    const apiResult: IApiResult = await apiSaveTestNew(
      route,
      key,
      title,
      textDto
    );
    if (apiResult.isSuccess) {
      setTestDtos([]);
      await getTest(section ?? '', dto?.key ?? '');
    } else {
      setAlertProps({
        message: API_STATUS_PROBLEM,
        severity: AlertSeverityEnum.error,
      });
    }
  };

  useEffect(() => {
    if (open) {
      getTest(section ?? '', dto?.key ?? '');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  return (
    <div>
      <Dialog
        open={open}
        fullWidth
        maxWidth="xl"
        style={{ userSelect: 'none' }}
        PaperProps={{
          sx: {
            backgroundColor: '#e7ebee',
            maxWidth: '100%',
            maxHeight: '100%',
            height: '100%',
          },
        }}
      >
        <BackDropDiv open={backDrop} />
        <MyAlert
          message={alertProps.message}
          severity={alertProps.severity}
          onClose={() => setAlertProps({ ...alertProps, message: '' })}
        />
        <DialogTitle>
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
          >
            <Typography
              style={{
                fontFamily: 'sans-serif',
                fontStyle: 'normal',
                fontWeight: 500,
                fontSize: '24px',
                color: '#000000',
                letterSpacing: 1,
              }}
              component="text"
            >
              {`Тесты - ${dto?.key} ${dto?.title}`}
            </Typography>
            <Stack direction="row" spacing={1}>
              <IconButton
                title="Сохранить"
                size="small"
                onClick={() =>
                  saveText(section ?? '', dto?.key ?? '', dto?.title ?? '')
                }
              >
                <SaveIcon fontSize="large" sx={{ color: '#2196f3' }} />
              </IconButton>
              <IconButton title="Закрыть" size="small" onClick={handleClose}>
                <CloseIcon fontSize="large" sx={{ color: '#000000' }} />
              </IconButton>
            </Stack>
          </Stack>
        </DialogTitle>
        <DialogContent
          sx={{
            padding: 0,
            '&:first-of-type': { paddingTop: 0 },
            display: 'flex',
            flexDirection: 'column',
            height: '100%',
            overflow: 'hidden', // ← Отключаем прокрутку диалога
            flex: 1,
          }}
        >
          <Grid
            container
            spacing={1}
            sx={{ height: '100%', overflow: 'hidden' }}
          >
            <Grid item xs={6} sx={{ height: '100%', overflow: 'auto' }}>
              <Stack padding="10px" spacing={1} sx={{ height: '100%' }}>
                <MyTextFiled
                  value={testString}
                  onChange={(e) => setTestString(e.target.value)}
                  multiLine
                  size="small"
                  label=""
                  // Если MyTextFiled не поддерживает sx, используйте style
                  // style={{ height: '100%' }}
                />
              </Stack>
            </Grid>
            <Grid item xs={6} sx={{ height: '100%', overflow: 'auto' }}>
              <Stack spacing={2} sx={{ height: '100%', overflow: 'auto' }}>
                {testDtos.map((item) => {
                  numTest += 1;
                  return (
                    <div key={numTest}>
                      <DialogTestItem
                        isEdit={isEdit === true}
                        num={numTest}
                        keyTitle={dto?.key ?? ''}
                        titleName={dto?.title ?? ''}
                        dto={item}
                      />
                      <Divider
                        orientation="horizontal"
                        sx={{
                          padding: '10px',
                          borderBottomWidth: 4,
                          borderColor: '#000000',
                          opacity: 0.6,
                        }}
                      />
                    </div>
                  );
                })}
              </Stack>
            </Grid>
          </Grid>
        </DialogContent>
      </Dialog>
    </div>
  );
};

DialogTestNew.defaultProps = {
  isEdit: false,
  dto: undefined,
};
